import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/Layout.jsx";
import { getAccount, masterSupabase } from "../lib/supabase.js";
import { useUserRole, hasPermission } from "../App.jsx";

const platformUrl = import.meta.env.VITE_SUPABASE_URL;

function useFonts() {
  useEffect(() => {
    [
      { id: "gfont-instrument-serif", href: "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap" },
      { id: "gfont-dm-sans", href: "https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;1,9..40,400&display=swap" },
    ].forEach((f) => {
      if (!document.getElementById(f.id)) {
        const link = document.createElement("link");
        link.id = f.id; link.rel = "stylesheet"; link.href = f.href;
        document.head.appendChild(link);
      }
    });
  }, []);
}

const L = {
  bg: "#fafaf8", text: "#1a1a1a", textSoft: "#444444", muted: "#8a8a8a",
  dim: "#c0bdb8", border: "#e5e3de", borderLight: "#f0eee9",
  surface: "#ffffff", inputBg: "#f6f5f2",
};
const sans = "'DM Sans', -apple-system, sans-serif";
const serif = "'Instrument Serif', Georgia, serif";
const btnGhost = { padding: "10px 20px", background: "none", border: `1px solid ${L.border}`, borderRadius: 8, color: L.muted, fontSize: 13, fontWeight: 500, cursor: "pointer", fontFamily: sans };

const TIERS = [
  { key: "starter", name: "Starter", price: 99, conversations: 500, features: ["500 conversations/mo", "AI chat widget", "Product catalog", "Location finder", "Basic analytics", "Email support"] },
  { key: "pro", name: "Pro", price: 299, conversations: 2500, features: ["2,500 conversations/mo", "All Starter features", "Choice of AI model", "Priority support", "Custom branding", "Advanced analytics"] },
  { key: "enterprise", name: "Enterprise", price: 599, conversations: 999999, features: ["Unlimited conversations", "All Pro features", "Bring your own API keys", "Custom integrations", "Dedicated support", "SLA guarantee"] },
];

const STATUS_STYLE = {
  active: { color: "#15803d", bg: "#f0fdf4", border: "#bbf7d0" },
  trialing: { color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe" },
  past_due: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  canceled: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
};

export default function Billing() {
  useFonts();
  const account = getAccount();
  const { role } = useUserRole();
  if (!hasPermission(role, "manageBilling")) return <Navigate to="/" />;
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    (async () => {
      if (!account?.id) return;
      const { data } = await masterSupabase.from("billing").select("*").eq("account_id", account.id).single();
      setBilling(data);
      setLoading(false);
    })();
  }, [account?.id]);

  const currentPlan = billing?.plan || account?.plan || "starter";
  const conversationsUsed = billing?.conversations_used || 0;
  const conversationLimit = billing?.conversation_limit || 500;
  const usagePercent = Math.min(100, Math.round((conversationsUsed / conversationLimit) * 100));
  const currentTier = TIERS.find((t) => t.key === currentPlan) || TIERS[0];
  const statusStyle = STATUS_STYLE[billing?.status] || STATUS_STYLE.active;

  const handleManageBilling = async () => {
    if (!billing?.stripe_customer_id) return;
    setUpgrading(true);
    try {
      const res = await fetch(`${platformUrl}/functions/v1/create-portal-session?account=${account.slug}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ return_url: window.location.href }) });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setUpgrading(false);
    } catch { setUpgrading(false); }
  };

  const handleUpgrade = async (tier) => {
    setUpgrading(true);
    try {
      const res = await fetch(`${platformUrl}/functions/v1/create-checkout-session?account=${account.slug}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ tier: tier.key, return_url: window.location.href }) });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else setUpgrading(false);
    } catch { setUpgrading(false); }
  };

  if (loading) return <Layout><div style={{ padding: 40, color: L.muted, textAlign: "center", background: L.bg, margin: -28, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: sans }}>Loading billing...</div></Layout>;

  return (
    <Layout>
      <style>{`@keyframes ov-up { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      <div style={{ minHeight: "calc(100vh - 56px)", background: L.bg, margin: -28, padding: "40px 44px", fontFamily: sans, color: L.text }}>
        {/* Header */}
        <div style={{ marginBottom: 32, animation: "ov-up 0.5s ease both" }}>
          <h1 style={{ fontSize: 36, fontWeight: 400, lineHeight: 1.1, margin: "0 0 8px", fontFamily: serif, fontStyle: "italic" }}>Billing</h1>
          <p style={{ margin: 0, fontSize: 13, color: L.muted }}>Manage your subscription and usage</p>
        </div>

        {/* Current plan */}
        <div style={{ background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12, padding: 28, marginBottom: 24, animation: "ov-up 0.5s ease 0.05s both" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}>
            <div>
              <div style={{ fontSize: 10, color: L.muted, textTransform: "uppercase", letterSpacing: "0.1em", fontWeight: 600, marginBottom: 8 }}>Current plan</div>
              <div style={{ fontSize: 28, fontWeight: 300, letterSpacing: "-0.02em" }}>{currentTier.name}</div>
              <div style={{ fontSize: 14, color: L.muted, marginTop: 4 }}>${currentTier.price}/month</div>
            </div>
            <div style={{ textAlign: "right" }}>
              <span style={{
                padding: "4px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: statusStyle.bg, color: statusStyle.color,
                border: `1px solid ${statusStyle.border}`, textTransform: "uppercase",
                letterSpacing: "0.04em",
              }}>
                {billing?.status || "pending"}
              </span>
              {billing?.current_period_end && <div style={{ fontSize: 11, color: L.dim, marginTop: 8 }}>Renews {new Date(billing.current_period_end).toLocaleDateString()}</div>}
              {billing?.trial_ends_at && billing.status === "trialing" && <div style={{ fontSize: 11, color: "#6d28d9", marginTop: 4 }}>Trial ends {new Date(billing.trial_ends_at).toLocaleDateString()}</div>}
            </div>
          </div>

          {/* Usage meter */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
              <span style={{ fontSize: 12, color: L.muted }}>Conversations this period</span>
              <span style={{ fontSize: 12, color: usagePercent > 80 ? "#b45309" : L.textSoft, fontWeight: 500, fontVariantNumeric: "tabular-nums" }}>
                {conversationsUsed.toLocaleString()} / {conversationLimit >= 999999 ? "Unlimited" : conversationLimit.toLocaleString()}
              </span>
            </div>
            <div style={{ height: 6, background: L.borderLight, borderRadius: 3 }}>
              <div style={{
                height: 6, borderRadius: 3,
                background: usagePercent > 90 ? "#dc2626" : usagePercent > 70 ? "#b45309" : L.text,
                width: `${Math.min(usagePercent, 100)}%`, transition: "width 0.3s ease",
              }} />
            </div>
            {usagePercent > 80 && <div style={{ fontSize: 12, color: "#b45309", marginTop: 8, lineHeight: 1.5 }}>You're approaching your conversation limit. Consider upgrading to avoid disruption.</div>}
          </div>

          {billing?.stripe_customer_id && (
            <button onClick={handleManageBilling} disabled={upgrading} style={{
              ...btnGhost, cursor: upgrading ? "not-allowed" : "pointer",
            }}>
              {upgrading ? "Loading..." : "Manage billing in Stripe →"}
            </button>
          )}
        </div>

        {/* Tier comparison */}
        <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16, animation: "ov-up 0.5s ease 0.08s both" }}>Available plans</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24, animation: "ov-up 0.5s ease 0.1s both" }}>
          {TIERS.map((tier) => {
            const isCurrent = tier.key === currentPlan;
            const isUpgrade = TIERS.indexOf(tier) > TIERS.findIndex((t) => t.key === currentPlan);
            return (
              <div key={tier.key} style={{
                background: L.surface, border: `1px solid ${isCurrent ? L.text : L.border}`,
                borderRadius: 12, padding: 26, position: "relative", overflow: "hidden",
                transition: "border-color 0.15s",
              }}>
                {isCurrent && (
                  <div style={{
                    position: "absolute", top: 14, right: 14, padding: "3px 10px", borderRadius: 20,
                    fontSize: 9, fontWeight: 600, background: L.inputBg, color: L.text,
                    border: `1px solid ${L.border}`, textTransform: "uppercase", letterSpacing: "0.06em",
                  }}>Current</div>
                )}
                <div style={{ fontSize: 17, fontWeight: 600, marginBottom: 6 }}>{tier.name}</div>
                <div style={{ marginBottom: 20 }}>
                  <span style={{ fontSize: 36, fontWeight: 300, letterSpacing: "-0.03em" }}>${tier.price}</span>
                  <span style={{ fontSize: 13, color: L.muted }}>/mo</span>
                </div>
                <div style={{ marginBottom: 24 }}>
                  {tier.features.map((f, i) => (
                    <div key={i} style={{ fontSize: 13, color: L.textSoft, padding: "5px 0", display: "flex", gap: 8, alignItems: "center" }}>
                      <span style={{ color: "#15803d", fontSize: 11, flexShrink: 0 }}>✓</span>{f}
                    </div>
                  ))}
                </div>
                {!isCurrent && (
                  <button onClick={() => handleUpgrade(tier)} disabled={upgrading} style={{
                    width: "100%", padding: "11px 0", borderRadius: 8, fontSize: 13,
                    fontWeight: 600, cursor: upgrading ? "not-allowed" : "pointer", fontFamily: sans,
                    background: isUpgrade ? L.text : "none",
                    border: isUpgrade ? "none" : `1px solid ${L.border}`,
                    color: isUpgrade ? L.bg : L.muted,
                    transition: "opacity 0.15s",
                  }}>
                    {upgrading ? "..." : isUpgrade ? "Upgrade" : "Switch"}
                  </button>
                )}
              </div>
            );
          })}
        </div>

        {/* Billing details */}
        {billing && (
          <div style={{
            background: L.surface, border: `1px solid ${L.border}`, borderRadius: 12,
            padding: 22, animation: "ov-up 0.5s ease 0.12s both",
          }}>
            <div style={{ fontSize: 11, color: L.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 16 }}>Billing details</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 18, fontSize: 13 }}>
              <div><span style={{ color: L.muted }}>Status: </span><span style={{ fontWeight: 500 }}>{billing.status}</span></div>
              <div><span style={{ color: L.muted }}>MRR: </span><span style={{ fontWeight: 500 }}>${(billing.mrr_cents || 0) / 100}/mo</span></div>
              <div><span style={{ color: L.muted }}>Currency: </span><span style={{ fontWeight: 500 }}>{(billing.currency || "usd").toUpperCase()}</span></div>
              {billing.current_period_start && <div><span style={{ color: L.muted }}>Period start: </span><span>{new Date(billing.current_period_start).toLocaleDateString()}</span></div>}
              {billing.current_period_end && <div><span style={{ color: L.muted }}>Period end: </span><span>{new Date(billing.current_period_end).toLocaleDateString()}</span></div>}
              {billing.discount_percent > 0 && <div><span style={{ color: L.muted }}>Discount: </span><span style={{ color: "#15803d", fontWeight: 500 }}>{billing.discount_percent}% {billing.discount_note ? `(${billing.discount_note})` : ""}</span></div>}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
